import React, { useEffect, useRef, useState } from 'react';
import { Box, Step, StepLabel, Stepper, Button, Paper, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Step1BasicDetails from './StepBasicDetails';
import Step2SelfAppraisal from './Step2SelfAppraisal';
import Step3ManagerReview from './Step3ManagerReview';
import Step4Ratings from './Step4Ratings';
import Step5PublishConfiguration from './Step5PublishConfiguration';
import Step6PeriodConfiguration from './Step6PeriodConfiguration';
import { Icon } from '@iconify/react';
import MotionWrapper from '../../../../../../components/MotionWrapper';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { createCycle, editCycle, getAllEvaluationcycle } from '../../../../../../redux/features/appraisalSlice';
import { useSnackbar } from 'notistack';

const steps = [
  'Basic Details',
  'Self Appraisal',
  'Manager Review',
  'Rating',
  'Publish Configuration',
  'Period Configuration',
];

const validationSchemas = [
  Yup.object({
    basicDetails: Yup.object({
      appraisalName: Yup.string().required('Appraisal name is required'),
      evaluationPeriod: Yup.object({
        startDate: Yup.date().required('Start date required'),
        endDate: Yup.date().required('End date required'),
      }),
      additionalNotes: Yup.string(),
    }),
  }),
  Yup.object(), // No validation for step 2 yet
];

const StepperWrapper = ({ slectedSections, setOpen, update, id, open }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef(null);
  const { cyclePreData, loading } = useSelector((state) => state.appraisal);

  // Dynamically construct steps and component mapping
  const allSteps = [
    { label: 'Basic Details', component: <Step1BasicDetails /> },
    ...(slectedSections.includes(1) ? [{ label: 'Self Appraisal', component: <Step2SelfAppraisal /> }] : []),
    ...(slectedSections.includes(2) ? [{ label: 'Manager Review', component: <Step3ManagerReview /> }] : []),
    { label: 'Rating', component: <Step4Ratings /> },
    {
      label: 'Publish Configuration',
      component: <Step5PublishConfiguration />,
    },
    { label: 'Period Configuration', component: <Step6PeriodConfiguration /> },
  ];

  const initialValues = open
    ? update
      ? cyclePreData
      : {
          basicDetails: {
            appraisalName: '',
            evaluationPeriod: {
              startDate: dayjs().format('YYYY-MM-DD'),
              endDate: dayjs().format('YYYY-MM-DD'),
            },
            additionalNotes: '',
          },
          selfAppraisal: {
            parameters: {
              KRA: {
                name: 'kra',
                included: false,
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
              Goals: {
                name: 'goals',
                included: false,
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
              Skill: {
                name: 'skills',
                included: false,
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
              Will: {
                name: 'will',
                included: false,
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
            },
          },
          managerReview: {
            parameters: {
              KRA: {
                included: false,
                name: 'kra',
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
              Goals: {
                included: false,
                name: 'goals',
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
              Skill: {
                included: false,
                name: 'skills',
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
              Will: {
                included: false,
                name: 'will',
                ratingsMandatory: false,
                commentsMandatory: false,
                ratings: false,
                comments: false,
              },
            },
          },
          ratings: {
            ratingType: 'numbers',
            range: {
              min: 1,
              max: 5,
            },
            decimalPoints: 1,
            includeNA: false,
            minimumCommentLength: 50,
            ratingScale: [
              {
                label: 'Outstanding',
                min: 4.5,
                max: 5,
              },
              {
                label: 'Excellent',
                min: 4,
                max: 4.4,
              },
              {
                label: 'Satisfactory',
                min: 3,
                max: 3.9,
              },
              {
                label: 'Needs Improvement',
                min: 2,
                max: 2.9,
              },
              {
                label: 'Unsatisfactory',
                min: 1,
                max: 1.9,
              },
            ],
            moduleWeighting: {
              enabled: false,
              weights: {
                Goals: 100,
                KRA: 100,
                Skill: 100,
                Will: 100,
              },
            },
          },
          publishConfiguration: {
            publishMethod: 'manual',
            outcomesShared: [],
            allowEmployeeFeedback: false,
          },
          periodConfiguration: {
            processPeriod: {
              startDate: dayjs().format('YYYY-MM-DD'),
              endDate: dayjs().format('YYYY-MM-DD'),
            },
            selfAppraisalPeriod: {
              startDate: null,
              endDate: null,
            },
            managerReviewPeriod: {
              startDate: null,
              endDate: null,
            },
            ratingPublishPeriod: {
              startDate: null,
              endDate: null,
            },
            feedBackPeriod: {
              startDate: null,
              endDate: null,
            },
            publishDate: null,
          },
        }
    : null;

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeStep]);

  const isLastStep = activeStep === allSteps.length - 1;

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      if (update) {
        const data = { ...values, id: id };
        dispatch(editCycle(data)).then((res) => {
          if (res.payload.status === 'failed' || res.payload.status === 'error') {
            enqueueSnackbar(res.payload.message, { variant: 'error' });
          } else if (res.payload.status === 'success') {
            enqueueSnackbar(res.payload.message, { variant: 'success' });
            setOpen(false);
            dispatch(getAllEvaluationcycle());
          }
        });
        actions.setSubmitting(false);
      } else {
        dispatch(createCycle(values)).then((res) => {
          if (res.payload.status === 'failed') {
            enqueueSnackbar(res.payload.message, { variant: 'error' });
          } else if (res.payload.status === 'success') {
            enqueueSnackbar(res.payload.message, { variant: 'success' });
            setOpen(false);
            dispatch(getAllEvaluationcycle());
          }
        });
        actions.setSubmitting(false);
      }

      // -----------------------------------------------------

      // ---------------------------------------------------------
    } else {
      setActiveStep((prev) => prev + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    const stepObj = allSteps[step];
    return (
      <MotionWrapper directions="bottom" delay={0.2}>
        {stepObj.component}
      </MotionWrapper>
    );
  };

  return (
    <Paper sx={{ p: 4 }} ref={containerRef}>
      <Typography variant="h5" gutterBottom>
        Appraisal Setup Form
      </Typography>

      <Box
        component={Paper}
        elevation={3}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'background.paper',
          py: 2,
          borderRadius: 3,
          mb: 2,
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            flexWrap: 'wrap',
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textAlign: 'center',
            },
          }}
        >
          {allSteps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                onClick={() => setActiveStep(index)}
                sx={{
                  cursor: 'pointer',
                  '& .MuiStepLabel-label': {
                    textDecoration: activeStep === index ? 'underline' : 'none',
                  },
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
        <Formik
          enableReinitialize={update ? true : false}
          initialValues={initialValues}
          validationSchema={validationSchemas[activeStep] || Yup.object()}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
        >
          {({ isSubmitting }) => (
            <Form>
              {getStepContent(activeStep)}

              <Box mt={4} display="flex" justifyContent="space-between">
                <Button
                  startIcon={<Icon icon="lets-icons:back" width="24" height="24" />}
                  sx={{ borderRadius: 3 }}
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((prev) => prev - 1)}
                >
                  Back
                </Button>
                <Button
                  startIcon={
                    isLastStep ? (
                      <Icon icon="lsicon:submit-filled" width="20" height="20" />
                    ) : (
                      <Icon icon="grommet-icons:link-next" width="20" height="20" />
                    )
                  }
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{ borderRadius: 3 }}
                >
                  {loading ? 'Please wait...' : isLastStep ? 'Submit' : 'Next'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Paper>
  );
};

export default StepperWrapper;
